import React, { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AdminRoute, SellerRoute, ClientRoute, ProfessionalRoute, RedirectByRole } from '@/app/guards/RoleGuards';
import { PageSkeleton, ErrorFallback, NotFoundPage } from '@/app/ui';
import { useAuth } from '@/contexts/AuthContext';
import { ToastProvider } from '@/shared/ui/sonner';
import PageTracker from '@/widgets/PageTracker';
import adminRoutes from './adminRoutes';
import authRoutes from './authRoutes';
import clientRoutes from './clientRoutes';
import legacyRedirects from './legacyRedirects';
import proRoutes from './proRoutes';
import publicRoutes from './publicRoutes';
import sellerRoutes from './sellerRoutes';

const ChatWidget = React.lazy(() => import('@/features/messaging/ChatWidget'));
const AdminLayout = React.lazy(() => import('@/app/layouts/AdminLayout'));
const SellerLayout = React.lazy(() => import('@/app/layouts/SellerLayout'));
const ClientLayout = React.lazy(() => import('@/app/layouts/ClientLayout'));
const ProLayout = React.lazy(() => import('@/app/layouts/ProLayout'));

function AuthenticatedChatWidget() {
  const { user } = useAuth();
  if (!user) return null;
  return (
    <Suspense fallback={null}>
      <ChatWidget />
    </Suspense>
  );
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <PageTracker />
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={(error, info) => {
          console.error('Uncaught error:', error, info.componentStack);
        }}
      >
        <Routes>
          {/* PUBLIC */}
          {publicRoutes.map((r) => (
            <Route key={r.path} {...r} />
          ))}

          {/* AUTH */}
          {authRoutes.map((r) => (
            <Route key={r.path} {...r} />
          ))}

          {/* DASHBOARD REDIRECT */}
          <Route path="/dashboard" element={<RedirectByRole />} />

          {/* ADMIN */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <Suspense fallback={<PageSkeleton />}>
                  <AdminLayout />
                </Suspense>
              </AdminRoute>
            }
          >
            {adminRoutes.map((r) => (
              <Route key={r.index ? '_index' : r.path} {...r} />
            ))}
          </Route>

          {/* SELLER */}
          <Route
            path="/seller"
            element={
              <SellerRoute>
                <Suspense fallback={<PageSkeleton />}>
                  <SellerLayout />
                </Suspense>
              </SellerRoute>
            }
          >
            {sellerRoutes.map((r) => (
              <Route key={r.index ? '_index' : r.path} {...r} />
            ))}
          </Route>

          {/* PRO */}
          <Route
            path="/pro"
            element={
              <ProfessionalRoute>
                <Suspense fallback={<PageSkeleton />}>
                  <ProLayout />
                </Suspense>
              </ProfessionalRoute>
            }
          >
            {proRoutes.map((r) => (
              <Route key={r.index ? '_index' : r.path} {...r} />
            ))}
          </Route>

          {/* CLIENT */}
          <Route
            path="/client"
            element={
              <ClientRoute>
                <Suspense fallback={<PageSkeleton />}>
                  <ClientLayout />
                </Suspense>
              </ClientRoute>
            }
          >
            {clientRoutes.map((r) => (
              <Route key={r.index ? '_index' : r.path} {...r} />
            ))}
          </Route>

          {/* LEGACY REDIRECTS */}
          {legacyRedirects.map((r) => (
            <Route key={r.path} {...r} />
          ))}

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </ErrorBoundary>

      <AuthenticatedChatWidget />
      <ToastProvider />
    </BrowserRouter>
  );
}
